import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiSearch, FiX } from 'react-icons/fi';
import './PostTests.css';

// Components
import Table from '../../../../components/Table/Table';

// Firebase
import {
  collection,
  endBefore,
  getDocs,
  limit,
  limitToLast,
  orderBy,
  query,
  startAfter,
  where
} from "firebase/firestore";
import { db } from '../../../../firebase';

// Get test
import { fetchPostTests, searchPostTestsByTitle } from '../../../../utils/firebase/fetchPostTests';
import { postTestMatchesSearch } from '../../../../utils/postTestSearch';

const PostTests = () => {
  const [testsDB, setTestsDB] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');

  // Firebase pagination
  const [pageSize, setPageSize] = useState(5);
  const [lastVisible, setLastVisible] = useState(null);
  const [firstVisible, setFirstVisible] = useState(null);

  // Keep a reference to the currently active test
  const [activeTest, setActiveTest] = useState(null);

  const columns = [
    { label: "Edit", accessor: "edit", sortable: true, date: false },
    { label: "Active", accessor: "active", sortable: false, date: false, link: false },
    { label: "Thumbnail", accessor: "thumbnailPreview", sortable: false, date: false, link: false },
    { label: "Title", accessor: "testTitle", sortable: true, date: false },
    { label: "Post Time", accessor: "postTime", sortable: true, date: true, sortbyOrder: "desc" },
    { label: "Total Loads", accessor: "totalLoads", sortable: true, date: false },
    { label: "Total Views", accessor: "totalViews", sortable: false, date: false, link: false },
    { label: "CTR", accessor: "ctr", sortable: true, date: false },
    { label: "Likes", accessor: "likes", sortable: false, date: false },
  ];

  // Helper function to format numbers and compute CTR
  const formatTestItem = (item) => {

    const thumbnailPreview = Array.isArray(item.thumbnails)
      ? item.thumbnails.find((thumbnail) => typeof thumbnail === 'string' && thumbnail.trim())
      : '';
    const totalLoads = Array.isArray(item.totalLoads) ? item.totalLoads.length : 0;
    const totalViews = Array.isArray(item.totalViews) ? item.totalViews.length : 0;
    // Calculate CTR as percentage with two decimal places
    const ctrValue = totalLoads && totalViews ? (totalViews / totalLoads) * 100 : 0;
    const ctr = `${ctrValue.toFixed(2)}%`;
    return { ...item, thumbnailPreview, ctr, ctrValue };
  };

  const markHighestCtr = (items) => {
    if (!items.length) return items;
    const highestCtr = Math.max(...items.map((item) => item.ctrValue || 0));
    return items.map((item) => ({
      ...item,
      isHighestCtr: Math.abs((item.ctrValue || 0) - highestCtr) < 0.000001,
    }));
  };

  const applyFormattedTests = (items) => {
    const formattedList = markHighestCtr(items.map(item => formatTestItem(item)));
    setTestsDB(formattedList);
    window.localStorage.setItem('testsDB', JSON.stringify(formattedList));
  };

  const mergeActiveTest = (items, activeDoc, searchTerm = '') => {
    let combinedList = items || [];
    const shouldIncludeActiveTest = activeDoc && (!searchTerm || postTestMatchesSearch(activeDoc, searchTerm));

    if (shouldIncludeActiveTest) {
      combinedList = combinedList.filter(item => item.id !== activeDoc.id);
      combinedList.unshift(activeDoc);
    }

    return combinedList;
  };

  // 1. A helper function to fetch the single active test if it exists.
  const fetchActiveTest = async () => {
    try {
      const q = query(
        collection(db, "postTests"),
        where("active", "==", true),
        limit(1)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        // We only ever expect one doc
        const doc = snap.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error("Error fetching active test:", error);
      return null;
    }
  };

  // 2. For the initial page load (and whenever pageSize changes):
  const handleGetTests = async () => {
    setLoading(true);
    setActiveSearchTerm('');
    setSearchText('');

    const [activeDoc, testsData] = await Promise.all([
      fetchActiveTest(),
      fetchPostTests(pageSize),
    ]);
    setActiveTest(activeDoc);

    const combinedList = mergeActiveTest(testsData.list, activeDoc);
    applyFormattedTests(combinedList);
    setLoading(testsData.loaded);
    setFirstVisible(testsData.firstVisible);
    setLastVisible(testsData.lastVisible);
  };

  const handleSearch = async (searchTerm) => {
    const trimmedSearchTerm = searchTerm.trim();

    if (!trimmedSearchTerm) {
      await handleGetTests();
      return;
    }

    setLoading(true);
    setActiveSearchTerm(trimmedSearchTerm);

    const [activeDoc, testsData] = await Promise.all([
      fetchActiveTest(),
      searchPostTestsByTitle(trimmedSearchTerm, pageSize),
    ]);
    setActiveTest(activeDoc);

    const combinedList = mergeActiveTest(testsData.list, activeDoc, trimmedSearchTerm);
    applyFormattedTests(combinedList);
    setLoading(testsData.loaded);
    setFirstVisible(null);
    setLastVisible(null);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    handleSearch(searchText);
  };

  const handleClearSearch = () => {
    handleGetTests();
  };

  useEffect(() => {
    if (activeSearchTerm) {
      handleSearch(activeSearchTerm);
      return;
    }

    handleGetTests();
    // eslint-disable-next-line
  }, [pageSize]);

  // 3. Update state helper to keep the active test pinned
  const updateState = (documents) => {
    if (!documents.empty) {
      const tempPosts = [];
      documents.forEach((document) => {
        tempPosts.push({
          id: document.id,
          ...document.data(),
        });
      });

      // If there's an active test, ensure it's at index 0
      let newData = tempPosts;
      if (activeTest) {
        newData = newData.filter(item => item.id !== activeTest.id);
        // Place active test at the top
        newData.unshift(activeTest);
      }

      // Format the new data to add commas and CTR before updating state
      const formattedData = markHighestCtr(newData.map(item => formatTestItem(item)));
      setTestsDB(formattedData);
    }

    // Update visible boundaries
    if (documents?.docs[0]) {
      setFirstVisible(documents.docs[0]);
    }
    if (documents?.docs[documents.docs.length - 1]) {
      setLastVisible(documents.docs[documents.docs.length - 1]);
    }
    setLoading(false);
  };

  // 4. Next page
  const nextPage = async () => {
    if (activeSearchTerm || !lastVisible) {
      return;
    }

    setLoading(true);

    const q = query(
      collection(db, "postTests"),
      orderBy("postTime", "desc"),
      startAfter(lastVisible),
      limit(pageSize)
    );

    const documents = await getDocs(q);
    updateState(documents);
  };

  // 5. Previous page
  const previousPage = async () => {
    if (activeSearchTerm || !firstVisible) {
      return;
    }

    setLoading(true);

    const q = query(
      collection(db, "postTests"),
      orderBy("postTime", "desc"),
      endBefore(firstVisible),
      limitToLast(pageSize)
    );

    const documents = await getDocs(q);
    updateState(documents);
  };

  return (
    <div className='posttests__container'>
      <h1 className='posttests__heading'>Post Tests</h1>

      <div>
        <button className='posttests__button' onClick={() => handleGetTests(null)}>
          Fetch new tests
        </button>
      </div>

      <div>
        <Link
          className='posttests__button'
          to='/newposttest'
        >
          CREATE new test
        </Link>
      </div>

      <form className='posttests__search' onSubmit={handleSearchSubmit}>
        <label className='posttests__search-label' htmlFor='post-test-search'>
          Search by title or animal
        </label>
        <div className='posttests__search-controls'>
          <input
            id='post-test-search'
            className='posttests__search-input'
            type='search'
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder='lion, leopard, elephant...'
          />
          <button
            className='posttests__button posttests__search-button'
            type='submit'
            title='Search post tests'
            aria-label='Search post tests'
          >
            <FiSearch aria-hidden='true' />
            Search
          </button>
          {activeSearchTerm ? (
            <button
              className='posttests__button posttests__search-button'
              type='button'
              onClick={handleClearSearch}
              title='Clear search'
              aria-label='Clear search'
            >
              <FiX aria-hidden='true' />
              Clear
            </button>
          ) : null}
        </div>
      </form>

      {activeSearchTerm && !loading ? (
        <p className='posttests__search-summary'>
          {testsDB.length} result{testsDB.length === 1 ? '' : 's'} for "{activeSearchTerm}"
        </p>
      ) : null}

      {loading ? (
        <div>Loading...</div>
      ) : (
        <Table
          caption="Latest Post Tests"
          data={testsDB}
          columns={columns}
          path='test'
        />
      )}

      <div className='posttests__pagination'>
        <button
          className='posttests__button'
          style={{ width: 100 }}
          onClick={previousPage}
          disabled={Boolean(activeSearchTerm) || !firstVisible}
        >
          Previous
        </button>
        <button
          className='posttests__button'
          style={{ width: 100 }}
          onClick={nextPage}
          disabled={Boolean(activeSearchTerm) || !lastVisible}
        >
          Next
        </button>

        <select
          style={{ width: 'auto', marginLeft: 20, padding: 5 }}
          name="pageSize"
          id="pageSize"
          onChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
          }}
        >
          <option value="5">5</option>
          <option value="30">30</option>
          <option value="42">42</option>
          <option value="54">54</option>
        </select>
      </div>
    </div>
  );
};

export default PostTests;
